/**
 * Composant d'upload de fichiers — design Carbon épuré
 */

import { useCallback, useState } from 'react'
import {
  Upload,
  Close,
  CheckmarkFilled,
  WarningAltFilled,
} from '@carbon/icons-react'
import { Button, Tile } from '@carbon/react'
import { validateFile } from '@sphere/shared/util'
import styles from './FileUpload.module.scss'

interface FileUploadProps {
  file: File | null
  onFileChange: (file: File | null) => void
  disabled?: boolean
  className?: string
}

export function FileUpload({
  file,
  onFileChange,
  disabled = false,
  className = '',
}: FileUploadProps) {
  const [dragActive, setDragActive] = useState(false)
  const [validation, setValidation] = useState<{
    isValid: boolean
    error?: string
  }>({ isValid: true })

  const validateAndSetFile = useCallback(
    (newFile: File | null) => {
      if (newFile) {
        const validationResult = validateFile(newFile)
        setValidation(validationResult)
        if (validationResult.isValid) {
          onFileChange(newFile)
        }
      } else {
        setValidation({ isValid: true })
        onFileChange(null)
      }
    },
    [onFileChange]
  )

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)
      if (disabled) return
      const files = Array.from(e.dataTransfer.files)
      if (files?.[0]) validateAndSetFile(files[0])
    },
    [disabled, validateAndSetFile]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return
      const files = e.target.files
      if (files?.[0]) validateAndSetFile(files[0])
    },
    [disabled, validateAndSetFile]
  )

  const handleRemoveFile = useCallback(() => {
    validateAndSetFile(null)
  }, [validateAndSetFile])

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return 'PDF'
    if (fileType.includes('word') || fileType.includes('document')) return 'DOC'
    if (fileType.includes('text')) return 'TXT'
    return 'FILE'
  }

  if (file) {
    return (
      <Tile className={`${styles.fileCard} ${className}`}>
        <div className={styles.fileRow}>
          <div className={styles.fileInfo}>
            <span className={styles.fileBadge}>{getFileIcon(file.type)}</span>
            <div className={styles.fileMeta}>
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.fileSize}>
                {formatFileSize(file.size)}
              </span>
            </div>
            <div className={styles.fileStatus}>
              {validation.isValid ? (
                <CheckmarkFilled size={20} className={styles.iconSuccess} />
              ) : (
                <WarningAltFilled size={20} className={styles.iconError} />
              )}
            </div>
          </div>
          {!disabled && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription="Retirer"
              renderIcon={Close}
              onClick={handleRemoveFile}
              className={styles.removeBtn}
            />
          )}
        </div>
        {!validation.isValid && validation.error && (
          <div className={styles.errorBox}>
            <WarningAltFilled size={16} />
            <span>{validation.error}</span>
          </div>
        )}
      </Tile>
    )
  }

  return (
    <div className={className}>
      <div
        className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''} ${disabled ? styles.dropZoneDisabled : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".pdf,.txt,.doc,.docx,.md"
          onChange={handleFileInput}
          disabled={disabled}
          className={styles.fileInput}
        />
        <div className={styles.dropContent}>
          <div className={styles.dropIcon}>
            <Upload size={20} />
          </div>
          <p className={styles.dropText}>
            Glissez un fichier ici ou{' '}
            <span className={styles.dropLink}>cliquez pour sélectionner</span>
          </p>
          <p className={styles.dropHint}>PDF, DOC, TXT, MD — max 50 MB</p>
        </div>
      </div>
    </div>
  )
}
